import {
  Checkbox,
  FormControlLabel,
  FormGroup,
  Modal,
  Button,
} from "@mui/material";
import { Dispatch, SetStateAction, useState } from "react";
import CloseIcon from "@mui/icons-material/Close";
import TextField from "@mui/material/TextField";
import { DataType } from "../types/General";
import { isString, isValidInput } from "../utils/validations";

interface AddRoleModalProps {
  open: boolean;
  onClose: () => void;
  setOpen: Dispatch<SetStateAction<boolean>>;
  permissions: DataType[];
  setPermissions: Dispatch<SetStateAction<DataType[]>>;
  roleName: string;
  setRoleName: Dispatch<SetStateAction<string>>;
  handleSubmit: () => void;
}

export default function AddNewRole({
  open,
  onClose,
  setOpen,
  permissions,
  setPermissions,
  roleName,
  setRoleName,
  handleSubmit,
}: AddRoleModalProps) {
  const [error, setError] = useState(false);

  const handlePermissionChange = (
    moduleId: number,
    permissionType: keyof DataType
  ) => {
    setPermissions((prevPermissions) =>
      prevPermissions.map((permission) => {
        if (permission.module_id === moduleId) {
          const updatedPermission = {
            ...permission,
            [permissionType]: !permission[permissionType],
          };

          // If can_view is false, set can_add_edit and can_be_delete to false
          if (permissionType === "can_view" && !updatedPermission.can_view) {
            updatedPermission.can_add_edit = false;
            updatedPermission.can_be_delete = false;
          }

          // If can_add_edit or can_be_delete is true, set can_view to true
          if (
            updatedPermission.can_add_edit ||
            updatedPermission.can_be_delete
          ) {
            updatedPermission.can_view = true;
          }

          return updatedPermission;
        }
        return permission;
      })
    );
  };

  return (
    <Modal
      className="modal addRole_modal"
      id="addRoleModal"
      aria-labelledby="addRole-modal-title"
      aria-describedby="addRole-modal-description"
      open={open}
      onClose={onClose}
    >
      <div className="modal-dialog">
        <div className="modal-body">
          <div className="btn-close" onClick={() => setOpen(false)}>
            <CloseIcon />
          </div>
          <div className="modal_title">
            <h2>Add New Role</h2>
            <p>Here you can add a new role</p>
          </div>
          <form action="">
            <div className="control_group">
              <TextField
                variant="outlined"
                placeholder="Role name"
                fullWidth
                hiddenLabel
                value={roleName}
                onChange={(val) => {
                  if (
                    isValidInput(val.target.value) &&
                    isString(val.target.value)
                  ) {
                    setRoleName(val.target.value);
                  }
                }}
              />
              {error && roleName === "" ? (
                <h6 className="err_msg">Role name is required</h6>
              ) : (
                ""
              )}
            </div>
            <h3>Roles permission</h3>

            <ul className="roles_list">
              {permissions?.map((permission) => (
                <li key={permission?.module_id}>
                  <p>{permission?.label}</p>
                  <FormGroup>
                    <FormControlLabel
                      control={<Checkbox />}
                      label="View"
                      checked={permission?.can_view}
                      onChange={() =>
                        handlePermissionChange(
                          permission?.module_id,
                          "can_view"
                        )
                      }
                    />
                    <FormControlLabel
                      control={<Checkbox />}
                      label="Add/Edit"
                      checked={permission?.can_add_edit}
                      onChange={() =>
                        handlePermissionChange(
                          permission?.module_id,
                          "can_add_edit"
                        )
                      }
                    />
                    <FormControlLabel
                      control={<Checkbox />}
                      label="Delete"
                      checked={permission?.can_be_delete}
                      onChange={() =>
                        handlePermissionChange(
                          permission?.module_id,
                          "can_be_delete"
                        )
                      }
                    />
                  </FormGroup>
                </li>
              ))}
            </ul>

            <div className="footer_btn_flex">
              <Button
                className="btn btn_highlight"
                onClick={() => {
                  setOpen(false);
                  setError(false);
                }}
              >
                Discard
              </Button>
              <Button
                className="btn btn_secondary"
                onClick={() => {
                  setError(true);
                  handleSubmit();
                }}
              >
                Submit
              </Button>
            </div>
          </form>
        </div>
      </div>
    </Modal>
  );
}
